import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DroneManufacturer {
  id: string;
  name: string;
  description: string | null;
  url: string | null;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-manufacturer-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './manufacturer-form.component.html',
  styleUrl: './manufacturer-form.component.scss'
})
export class ManufacturerFormComponent implements OnInit {
  manufacturerForm: FormGroup;
  loading = false;
  loadingManufacturer = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  manufacturerId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.manufacturerForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      url: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]],
      logoUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]]
    });
  }

  ngOnInit(): void {
    this.manufacturerId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.manufacturerId && this.manufacturerId !== 'create';

    if (this.isEditMode) {
      this.loadManufacturer();
    }
  }

  async loadManufacturer(): Promise<void> {
    this.loadingManufacturer = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneManufacturer }>(
          `${environment.apiUrl}/admin/drone-manufacturers/${this.manufacturerId}`
        )
      );

      if (response.success && response.data) {
        const manufacturer = response.data;

        this.manufacturerForm.patchValue({
          name: manufacturer.name,
          description: manufacturer.description || '',
          url: manufacturer.url || '',
          logoUrl: manufacturer.logoUrl || ''
        });
      }
    } catch (error) {
      console.error('Failed to load manufacturer:', error);
      this.errorMessage = 'Failed to load manufacturer details';
    } finally {
      this.loadingManufacturer = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.manufacturerForm.invalid) {
      this.manufacturerForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formData = this.manufacturerForm.value;

      if (this.isEditMode) {
        await firstValueFrom(
          this.http.put(
            `${environment.apiUrl}/admin/drone-manufacturers/${this.manufacturerId}`,
            formData
          )
        );

        this.successMessage = 'Manufacturer updated successfully';

        setTimeout(() => {
          this.router.navigate(['/drone-catalog', this.manufacturerId]);
        }, 1500);
      } else {
        const response = await firstValueFrom(
          this.http.post<{ success: boolean; data: DroneManufacturer }>(
            `${environment.apiUrl}/admin/drone-manufacturers`,
            formData
          )
        );

        this.router.navigate(['/success'], {
          state: {
            entities: [
              { type: 'manufacturer', action: 'created', name: formData.name },
            ],
            message: 'Drone manufacturer created successfully',
            details: {
              'Name': formData.name,
              'Website': formData.url || 'Not provided',
              'Description': formData.description ? 'Provided' : 'Not provided'
            },
            createAnotherRoute: '/drone-catalog/create'
          }
        });
      }
    } catch (error: any) {
      console.error('Save manufacturer error:', error);
      this.errorMessage = this.isEditMode
        ? 'Failed to update manufacturer. Please try again.'
        : 'Failed to create manufacturer. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    if (this.isEditMode) {
      this.router.navigate(['/drone-catalog', this.manufacturerId]);
    } else {
      this.router.navigate(['/drone-catalog']);
    }
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Manufacturer' : 'Create Manufacturer';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Manufacturer';
  }
}
